/* assets/prio-service.js */
(() => {
  // =========================
  // Config (tweak as needed)
  // =========================
  const CONFIG = {
    // Your PRIO UI container is rendered by the snippet with this attribute
    boxSelector: '[data-prio-toggle]',
    // The mini-cart form (submit is intercepted to sync PRIO with server)
    miniCartFormSelector: '#mini-cart-form',
    // Elements to repaint with the cart subtotal (first match wins)
    subtotalTargets: [
      '[data-cart-subtotal]',                 // recommended hook you add around total
      '.totals__subtotal-value .money',
      '.cart-subtotal .money',
      '.cart__subtotal .money'
    ],
    // If true, UI will instantly add PRIO price to displayed subtotal (UX nicety).
    // Server cart will still be kept authoritative on submit & when toggling.
    fakeSubtotalBump: false,
    // Wait times to let Shopify settle totals/discounts after updates
    settleDelayMs: 150,
    resyncDelayMs: 250
  };

  // =========================
  // Small utilities
  // =========================
  const sleep = (ms) => new Promise(r => setTimeout(r, ms));
  const debounce = (fn, ms=150) => {
    let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  };

  async function fetchJSON(url, opts) {
    try {
      const res = await fetch(url, { credentials: 'same-origin', ...opts });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return await res.json();
    } catch (err) {
      console.error('[PRIO] fetchJSON error:', err);
      return null;
    }
  }

  async function cartJSON() {
    return fetchJSON('/cart.js', { headers: { 'Accept': 'application/json' } });
  }

  async function variantJSON(id) {
    return fetchJSON(`/variants/${id}.js`, { headers: { 'Accept': 'application/json' } });
  }

  function formatMoneyFromCents(cents) {
    // Prefer Shopify.formatMoney if present, respecting store money_format
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      const fmt = (Shopify.money_format || '${{amount}}');
      try { return Shopify.formatMoney(cents, fmt); } catch {}
    }
    // Fallback: try active currency or EUR
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((cents || 0) / 100);
  }

  function updateSubtotalText(subtotalCents) {
    let any = false;
    const text = formatMoneyFromCents(subtotalCents);
    CONFIG.subtotalTargets.forEach(sel => {
      document.querySelectorAll(sel).forEach(el => {
        el.textContent = text;
        try { el.setAttribute('data-money-cents', String(subtotalCents)); } catch {}
        any = true;
      });
    });
    if (!any) {
      document.documentElement.dispatchEvent(new CustomEvent('prio:subtotal:update', {
        bubbles: true, detail: { subtotalCents, formatted: text }
      }));
    }
  }

  async function setVariantQty(variantId, qty) {
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates: { [variantId]: qty } })
      });
      return await res.json();
    } catch (e) {
      console.error('[PRIO] setVariantQty error:', e);
      return null;
    }
  }

  function cartHasVariant(cart, variantId) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some(it => Number(it.variant_id) === Number(variantId));
  }

  // =========================
  // Controller (per box)
  // =========================
  class PrioController {
    constructor(box) {
      this.box = box;
      this.variantId = Number(box?.dataset?.prioVariantId || 0);
      this.checkbox = box.querySelector('.cartBodyUpgrade__checkbox');
      this.priceCents = null;
      this.syncing = false;

      if (!this.variantId || !this.checkbox) {
        console.warn('[PRIO] Missing variantId or checkbox in box', box);
        return;
      }

      this.init();
    }

    async init() {
      // Fetch price for optional fake bump
      const v = await variantJSON(this.variantId);
      if (v && typeof v.price === 'number') this.priceCents = v.price;

      // Initial sync with server cart
      await this.syncCheckboxFromServer();
      await this.repaintSubtotal();

      // Bind UI events
      this.checkbox.addEventListener('change', () => this.onToggle());

      // Repaint on typical cart interactions
      document.addEventListener('change', debounce((e) => {
        const t = e.target;
        if (!t) return;
        const isQty = t.matches('input[name^="updates"], input[data-cart-quantity], .quantity__input');
        if (isQty) this.resyncSoon();
      }, 150));

      document.addEventListener('click', debounce((e) => {
        const t = e.target && e.target.closest('[data-cart-remove], .cart__remove, .quantity__button, [name="update"], [data-cart-update]');
        if (t) this.resyncSoon();
      }, 150));

      // Listen for common custom events if the theme dispatches them
      ['cart:refresh', 'cart:updated', 'ajaxCart:afterUpdate', 'shopify:section:load']
        .forEach(ev => document.addEventListener(ev, () => this.resyncSoon()));

      // Intercept mini-cart form submit to ensure server truth matches toggle
      const form = document.querySelector(CONFIG.miniCartFormSelector);
      if (form) {
        form.addEventListener('submit', async (e) => {
          // If fake mode is on, or simply to be safe, ensure server matches UI before checkout
          if (CONFIG.fakeSubtotalBump) {
            e.preventDefault();
            await this.applyToggleToServer();
            setTimeout(() => form.submit(), 120);
          }
        });
      }

      // expose for debugging
      window.PRIO = window.PRIO || {};
      window.PRIO.controllers = window.PRIO.controllers || new Set();
      window.PRIO.controllers.add(this);
    }

    async onToggle() {
      if (this.syncing) return;
      this.syncing = true;
      this.checkbox.disabled = true;

      await this.applyToggleToServer();
      await sleep(CONFIG.settleDelayMs);
      await this.syncCheckboxFromServer();
      await this.repaintSubtotal();

      this.checkbox.disabled = false;
      this.syncing = false;

      document.documentElement.dispatchEvent(new CustomEvent('prio:toggled', {
        bubbles: true, detail: { checked: this.checkbox.checked, variantId: this.variantId }
      }));
    }

    async applyToggleToServer() {
      const want = this.checkbox.checked ? 1 : 0;
      await setVariantQty(this.variantId, want);
    }

    async syncCheckboxFromServer() {
      const cart = await cartJSON();
      if (!cart) return;
      this.checkbox.checked = cartHasVariant(cart, this.variantId);
    }

    async repaintSubtotal() {
      const cart = await cartJSON();
      if (!cart) return;

      let subtotal = cart.subtotal_price; // cents

      if (CONFIG.fakeSubtotalBump && this.checkbox.checked && typeof this.priceCents === 'number') {
        subtotal += this.priceCents;
      }

      updateSubtotalText(subtotal);

      document.documentElement.dispatchEvent(new CustomEvent('prio:cart:update', {
        bubbles: true, detail: { cart }
      }));
    }

    resyncSoon() {
      setTimeout(() => { this.syncCheckboxFromServer(); this.repaintSubtotal(); }, CONFIG.resyncDelayMs);
    }
  }

  // =========================
  // Bootstrapping
  // =========================
  function initAll() {
    document.querySelectorAll(CONFIG.boxSelector).forEach(box => {
      // prevent double init
      if (box.__prioBound) return;
      box.__prioBound = true;
      new PrioController(box);
    });
  }

  // Initial run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Observe dynamic mini-cart re-renders and section loads
  const observer = new MutationObserver(debounce(() => initAll(), 100));
  observer.observe(document.documentElement, { subtree: true, childList: true });

  // Also listen to common theme events that redraw cart content
  ['shopify:section:load', 'cart:updated', 'ajaxCart:afterUpdate'].forEach(ev =>
    document.addEventListener(ev, initAll)
  );
})();
